import { useState, useMemo } from 'react'
import {
  CheckCircle, AlertCircle, XCircle, HelpCircle, RefreshCw,
  Search, Download, ChevronDown, ChevronRight, Wrench,
} from 'lucide-react'
import { usePlatformFeatures, useVerifyPlatformFeature } from '@/hooks/useApi'

type Cap = {
  platform_feature_id: string
  name: string
  description: string | null
  category: string
  criticality: string
  status: string
  visibility: string
  verification_status: string
  last_verified: string | null
  rto_minutes: number
  rpo_minutes: number
  updated_at: string
  owner_primary?: string
}

const VERIFICATION: Record<string, { label: string; color: string; Icon: React.ComponentType<{ className?: string }> }> = {
  verified: { label: 'Verified', color: 'text-green-600 dark:text-green-400', Icon: CheckCircle },
  warning: { label: 'Warning', color: 'text-amber-600 dark:text-amber-400', Icon: AlertCircle },
  failed: { label: 'Failed', color: 'text-red-600 dark:text-red-400', Icon: XCircle },
  unknown: { label: 'Unknown', color: 'text-gray-400 dark:text-white/30', Icon: HelpCircle },
  pending: { label: 'Pending', color: 'text-blue-600 dark:text-blue-400', Icon: RefreshCw },
}

const CRIT_COLORS: Record<string, string> = {
  critical: 'text-red-600 dark:text-red-400 bg-red-600/10 dark:bg-red-400/10',
  high: 'text-amber-600 dark:text-amber-400 bg-amber-600/10 dark:bg-amber-400/10',
  medium: 'text-blue-600 dark:text-blue-400 bg-blue-600/10 dark:bg-blue-400/10',
  low: 'text-gray-400 dark:text-white/30 bg-gray-400/10 dark:bg-white/5',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'text-green-600 dark:text-green-400',
  experimental: 'text-blue-600 dark:text-blue-400',
  deprecated: 'text-amber-600 dark:text-amber-400',
  planned: 'text-gray-400 dark:text-white/30',
  retired: 'text-gray-300 dark:text-white/20',
  removed: 'text-red-400/60 dark:text-red-400/60',
}

function exportCSV(tools: Cap[]) {
  const header = 'name,criticality,status,verification_status,last_verified,owner,rto_minutes,rpo_minutes'
  const rows = tools.map(t =>
    [
      `"${t.name}"`, t.criticality, t.status, t.verification_status,
      t.last_verified || '', `"${t.owner_primary || ''}"`, t.rto_minutes, t.rpo_minutes,
    ].join(',')
  )
  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `platform-features-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

import type React from 'react'

function ToolRow({ cap, onVerify, verifying }: { cap: Cap; onVerify: () => void; verifying: boolean }) {
  const [open, setOpen] = useState(false)
  const v = VERIFICATION[cap.verification_status] || VERIFICATION.unknown
  return (
    <div className="border-b border-gray-200 dark:border-white/5 last:border-0">
      <button
        className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-100 dark:hover:bg-white/[0.03] text-left transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        {open
          ? <ChevronDown className="w-3 h-3 text-gray-400 dark:text-white/20 shrink-0" />
          : <ChevronRight className="w-3 h-3 text-gray-400 dark:text-white/20 shrink-0" />}
        <span className="flex-1 text-[11px] font-mono text-gray-800 dark:text-white/80 truncate min-w-0">{cap.name}</span>
        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${CRIT_COLORS[cap.criticality] || 'text-gray-400 dark:text-white/30 bg-gray-400/10 dark:bg-white/5'}`}>
          {cap.criticality}
        </span>
        <span className={`text-[10px] font-mono shrink-0 w-20 text-right hidden sm:block ${STATUS_COLORS[cap.status] || 'text-gray-400 dark:text-white/30'}`}>
          {cap.status}
        </span>
        <span className={`flex items-center gap-1 text-[10px] font-mono shrink-0 w-20 justify-end ${v.color}`}>
          <v.Icon className="w-3 h-3" />
          <span className="hidden sm:inline">{v.label}</span>
        </span>
        <span className="text-[9px] text-gray-400 dark:text-white/20 font-mono shrink-0 w-24 text-right hidden md:block">
          {cap.last_verified ? new Date(cap.last_verified).toLocaleDateString() : '—'}
        </span>
      </button>
      {open && (
        <div className="px-9 pb-3 pt-1 space-y-1.5 bg-gray-50 dark:bg-white/[0.01]">
          {cap.description && (
            <p className="text-[11px] text-gray-600 dark:text-white/50 leading-relaxed">{cap.description}</p>
          )}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] text-gray-500 dark:text-white/40 font-mono">
            <span>visibility: <span className="text-gray-700 dark:text-white/60">{cap.visibility}</span></span>
            {cap.owner_primary && <span>owner: <span className="text-gray-700 dark:text-white/60">{cap.owner_primary}</span></span>}
            {cap.rto_minutes > 0 && <span>RTO: <span className="text-gray-700 dark:text-white/60">{cap.rto_minutes}m</span></span>}
            {cap.rpo_minutes > 0 && <span>RPO: <span className="text-gray-700 dark:text-white/60">{cap.rpo_minutes}m</span></span>}
            {cap.last_verified && (
              <span>last verified: <span className="text-gray-700 dark:text-white/60">{new Date(cap.last_verified).toLocaleString()}</span></span>
            )}
          </div>
          <button
            onClick={e => { e.stopPropagation(); onVerify() }}
            disabled={verifying}
            className="text-[10px] px-2.5 py-1 rounded bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white/90 transition-colors disabled:opacity-40 font-mono"
          >
            {verifying ? 'Verifying…' : 'Verify Now'}
          </button>
        </div>
      )}
    </div>
  )
}

export function ToolsRegistryPanel() {
  const [search, setSearch] = useState('')
  const [criticality, setCriticality] = useState('all')
  const [verStatus, setVerStatus] = useState('all')
  const [verifying, setVerifying] = useState<string | null>(null)
  const { data, isLoading, error } = usePlatformFeatures()
  const verifyMutation = useVerifyPlatformFeature()

  const tools = useMemo(() => {
    const list = (data?.platform_features as Cap[]) || []
    return list.filter(t => {
      if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false
      if (criticality !== 'all' && t.criticality !== criticality) return false
      if (verStatus !== 'all' && t.verification_status !== verStatus) return false
      return true
    })
  }, [data, search, criticality, verStatus])

  const handleVerify = async (id: string) => {
    setVerifying(id)
    try { await verifyMutation.mutateAsync(id) } finally { setVerifying(null) }
  }

  const summary = data?.summary

  return (
    <div className="relative flex flex-col h-full rounded-2xl border overflow-hidden bg-white/75 border-white/60 shadow-[0_4px_24px_rgba(0,0,0,0.07),0_1px_4px_rgba(0,0,0,0.04)] backdrop-blur-md dark:bg-black/[0.28] dark:border-white/[0.10] dark:shadow-[0_4px_32px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.06)] dark:backdrop-blur-md">
      <div className="px-4 pt-4 pb-3 border-b border-gray-200 dark:border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-gray-500 dark:text-white/40" />
            <h2 className="text-xs font-bold text-gray-700 dark:text-white/70 uppercase tracking-widest">Platform Features</h2>
          </div>
          <div className="flex items-center gap-2">
            {summary && (
              <div className="flex gap-3">
                {Object.entries(summary.by_verification).map(([k, v]) => {
                  const vd = VERIFICATION[k] || VERIFICATION.unknown
                  return (
                    <span key={k} className={`flex items-center gap-1 text-[10px] font-mono ${vd.color}`}>
                      <vd.Icon className="w-3 h-3" />{v as number}
                    </span>
                  )
                })}
              </div>
            )}
            {data && (
              <span className="text-[10px] font-mono text-gray-400 dark:text-white/30">{tools.length}/{data.total}</span>
            )}
            {data && data.platform_features.length > 0 && (
              <button
                onClick={() => exportCSV(data.platform_features as Cap[])}
                className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-500 dark:text-white/40 hover:text-gray-800 dark:hover:text-white/70 transition-colors font-mono"
                title="Export as CSV"
              >
                <Download className="w-3 h-3" />CSV
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-32">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 dark:text-white/20" />
            <input
              type="text"
              placeholder="Search tools…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-6 pr-2 py-1 text-[11px] font-mono bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded text-gray-800 dark:text-white/70 placeholder:text-gray-400 dark:placeholder:text-white/20 focus:outline-none focus:border-gray-400 dark:focus:border-white/20"
            />
          </div>
          <select
            value={criticality}
            onChange={e => setCriticality(e.target.value)}
            className="glass text-[11px] font-mono rounded px-2 py-1 text-gray-700 dark:text-white/60 focus:outline-none"
          >
            <option value="all">All criticality</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={verStatus}
            onChange={e => setVerStatus(e.target.value)}
            className="glass text-[11px] font-mono rounded px-2 py-1 text-gray-700 dark:text-white/60 focus:outline-none"
          >
            <option value="all">All verification</option>
            <option value="verified">Verified</option>
            <option value="warning">Warning</option>
            <option value="failed">Failed</option>
            <option value="unknown">Unknown</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="px-4 py-8 text-center text-xs text-gray-400 dark:text-white/30 font-mono">Loading platform features…</div>
        )}
        {error && (
          <div className="px-4 py-8 text-center text-xs text-gray-400 dark:text-white/30 font-mono">Platform features pending backend deployment</div>
        )}
        {!isLoading && !error && tools.length === 0 && (
          <div className="px-4 py-8 text-center text-xs text-gray-400 dark:text-white/30 font-mono">No features match the current filters</div>
        )}
        {tools.map(cap => (
          <ToolRow
            key={cap.platform_feature_id}
            cap={cap}
            onVerify={() => handleVerify(cap.platform_feature_id)}
            verifying={verifying === cap.platform_feature_id}
          />
        ))}
      </div>
    </div>
  )
}
